// src/components/notifications/NotificationCenter.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';

const NotificationCenter = () => {
    usePageTitle('Notifications');
    const navigate = useNavigate();
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, active, inactive
    const [page, setPage] = useState(0);
    const [totalNotifications, setTotalNotifications] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        title: '',
        message: '',
        type: 'info',
        action_url: '',
        action_label: '',
        expires_at: ''
    });

    const limit = 20;

    useEffect(() => {
        fetchNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, page]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await notificationService.getNotifications({
                limit,
                offset: page * limit,
                status: filter
            });

            setNotifications(response.notifications || []);
            setTotalNotifications(response.total || 0);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            showMessage('error', 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            // Convert is_read to boolean (API returns "0" or "1" as string)
            const isRead = notification.is_read === true || notification.is_read === 1 || notification.is_read === "1";

            if (!isRead) {
                await notificationService.markAsRead(notification.id);
                setNotifications(prev =>
                    prev.map(n => n.id === notification.id ? { ...n, is_read: 1 } : n)
                );
            }

            if (notification.action_url) {
                navigate(notification.action_url);
            }
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            showMessage('success', 'All notifications marked as read');
        } catch (error) {
            console.error('Failed to mark all as read:', error);
            showMessage('error', 'Failed to mark all as read');
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleViewNotification = (e, notification) => {
        e.stopPropagation();
        setSelectedNotification(notification);
        setShowModal(true);

        // Mark as read when viewing
        const isRead = notification.is_read === true || notification.is_read === 1 || notification.is_read === "1";
        if (!isRead) {
            notificationService.markAsRead(notification.id);
            setNotifications(prev =>
                prev.map(n => n.id === notification.id ? { ...n, is_read: 1 } : n)
            );
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedNotification(null);
    };

    const handleEditNotification = (e, notification) => {
        e.stopPropagation();
        setEditFormData({
            title: notification.title || '',
            message: notification.message || '',
            type: notification.type || 'info',
            action_url: notification.action_url || '',
            action_label: notification.action_label || '',
            expires_at: notification.expires_at ? notification.expires_at.split(' ')[0] : ''
        });
        setSelectedNotification(notification);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedNotification(null);
        setEditFormData({
            title: '',
            message: '',
            type: 'info',
            action_url: '',
            action_label: '',
            expires_at: ''
        });
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateNotification = async (e) => {
        e.preventDefault();

        if (!editFormData.title || !editFormData.message) {
            showMessage('error', 'Title and message are required');
            return;
        }

        try {
            setLoading(true);
            await notificationService.updateNotification(selectedNotification.id, editFormData);

            // Update local state
            setNotifications(prev =>
                prev.map(n => n.id === selectedNotification.id
                    ? { ...n, ...editFormData }
                    : n
                )
            );

            showMessage('success', 'Notification updated successfully');
            closeEditModal();
            fetchNotifications();
        } catch (error) {
            console.error('Failed to update notification:', error);
            showMessage('error', error.response?.data?.message || 'Failed to update notification');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteNotification = async (e, notification) => {
        e.stopPropagation();
        const confirmed = window.confirm('Delete this notification message?');
        if (!confirmed) return;

        try {
            await notificationService.deleteNotification(notification.id);
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
            setTotalNotifications(prev => Math.max(0, prev - 1));
            showMessage('success', 'Notification deleted successfully');
        } catch (error) {
            console.error('Failed to delete notification:', error);
            showMessage('error', error.response?.data?.message || 'Failed to delete notification');
        }
    };

    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
        return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return 'fas fa-check-circle';
            case 'warning':
                return 'fas fa-exclamation-triangle';
            case 'error':
                return 'fas fa-times-circle';
            case 'announcement':
                return 'fas fa-bullhorn';
            default:
                return 'fas fa-info-circle';
        }
    };

    const unreadCount = notifications.filter(n => {
        const isRead = n.is_read === true || n.is_read === 1 || n.is_read === "1";
        return !isRead;
    }).length;
    const totalPages = Math.ceil(totalNotifications / limit);

    return (
        <div className="notification-center">
            <div className="page-header">
                <div className="header-content">
                    <h1>
                        <i className="fas fa-bell"></i>
                        Notifications
                    </h1>
                    <p>Stay updated with your latest notifications</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn btn-success"
                        onClick={() => navigate('/notifications/broadcast')}
                    >
                        <i className="fas fa-bullhorn"></i>
                        Send Notification
                    </button>
                    {unreadCount > 0 && (
                        <button
                            className="btn btn-primary"
                            onClick={handleMarkAllAsRead}
                        >
                            <i className="fas fa-check-double"></i>
                            Mark All as Read
                        </button>
                    )}
                </div>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            <div className="notifications-container">
                <div className="notifications-header">
                    <div className="filter-tabs">
                        <button
                            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => {
                                setFilter('all');
                                setPage(0);
                            }}
                        >
                            All ({totalNotifications})
                        </button>
                        <button
                            className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
                            onClick={() => {
                                setFilter('active');
                                setPage(0);
                            }}
                        >
                            Active
                        </button>
                        <button
                            className={`filter-tab ${filter === 'inactive' ? 'active' : ''}`}
                            onClick={() => {
                                setFilter('inactive');
                                setPage(0);
                            }}
                        >
                            Inactive
                        </button>
                    </div>
                </div>

                <div className="notifications-body">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner-large"></div>
                            <p>Loading notifications...</p>
                        </div>
                    ) : notifications.length > 0 ? (
                        <>
                            <div className="notifications-list">
                                {notifications.map((notification) => {
                                    const isRead = notification.is_read === true || notification.is_read === 1 || notification.is_read === "1";
                                    const canDelete = user?.user_type === 'core_admin' || Number(notification.sender_id) === Number(user?.user_id);
                                    const senderType = String(notification.sender_type || '');
                                    const canEdit = user?.user_type === 'core_admin' || (Number(notification.sender_id) === Number(user?.user_id) && !['core_admin', 'system'].includes(senderType));
                                    return (
                                    <div
                                        key={notification.id}
                                        className={`notification-card ${isRead ? 'read' : 'unread'}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className={`notification-icon ${notification.type}`}>
                                            <i className={getNotificationIcon(notification.type)}></i>
                                        </div>
                                        <div className="notification-content">
                                            <div className="notification-header-row">
                                                <h3 className="notification-title">{notification.title}</h3>
                                                {!isRead && (
                                                    <span className="unread-badge">New</span>
                                                )}
                                            </div>
                                            <p className="notification-message">{notification.message}</p>
                                            <div className="notification-meta">
                                                <span className="notification-time">
                                                    <i className="fas fa-clock"></i>
                                                    {formatRelativeTime(notification.created_at)}
                                                </span>
                                                {notification.sender_name && (
                                                    <span className="notification-sender">
                                                        <i className="fas fa-user"></i>
                                                        From: {notification.sender_name}
                                                    </span>
                                                )}
                                                {notification.action_label && (
                                                    <span className="notification-action">
                                                        <i className="fas fa-arrow-right"></i>
                                                        {notification.action_label}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="notification-actions">
                                            <button
                                                className="action-btn view-btn"
                                                onClick={(e) => handleViewNotification(e, notification)}
                                                title="View Details"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                            {canEdit && (
                                                <button
                                                    className="action-btn edit-btn"
                                                    onClick={(e) => handleEditNotification(e, notification)}
                                                    title="Edit Notification"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    className="action-btn delete-btn"
                                                    onClick={(e) => handleDeleteNotification(e, notification)}
                                                    title="Delete Notification"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination">
                                    <button
                                        className="pagination-btn"
                                        disabled={page === 0}
                                        onClick={() => setPage(p => p - 1)}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                        Previous
                                    </button>
                                    <span className="pagination-info">
                                        Page {page + 1} of {totalPages}
                                    </span>
                                    <button
                                        className="pagination-btn"
                                        disabled={page >= totalPages - 1}
                                        onClick={() => setPage(p => p + 1)}
                                    >
                                        Next
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="empty-state">
                            <i className="fas fa-bell-slash"></i>
                            <h3>No notifications</h3>
                            <p>You're all caught up! No notifications to display for this filter.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit Notification Modal */}
            {showEditModal && selectedNotification && (
                <div className="modal-overlay" onClick={closeEditModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <i className="fas fa-edit"></i>
                                Edit Notification
                            </h2>
                            <button className="modal-close" onClick={closeEditModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleUpdateNotification}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="edit-title">
                                        Title <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-title"
                                        name="title"
                                        value={editFormData.title}
                                        onChange={handleEditFormChange}
                                        required
                                        className="form-input"
                                        placeholder="Enter notification title"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="edit-message">
                                        Message <span className="required">*</span>
                                    </label>
                                    <textarea
                                        id="edit-message"
                                        name="message"
                                        value={editFormData.message}
                                        onChange={handleEditFormChange}
                                        required
                                        className="form-textarea"
                                        placeholder="Enter notification message"
                                        rows="4"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="edit-type">
                                        Type <span className="required">*</span>
                                    </label>
                                    <select
                                        id="edit-type"
                                        name="type"
                                        value={editFormData.type}
                                        onChange={handleEditFormChange}
                                        required
                                        className="form-select"
                                    >
                                        <option value="info">Info</option>
                                        <option value="success">Success</option>
                                        <option value="warning">Warning</option>
                                        <option value="error">Error</option>
                                        <option value="announcement">Announcement</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="edit-action-label">
                                        Action Label (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-action-label"
                                        name="action_label"
                                        value={editFormData.action_label}
                                        onChange={handleEditFormChange}
                                        className="form-input"
                                        placeholder="e.g., View Details, Learn More"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="edit-action-url">
                                        Action URL (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        id="edit-action-url"
                                        name="action_url"
                                        value={editFormData.action_url}
                                        onChange={handleEditFormChange}
                                        className="form-input"
                                        placeholder="e.g., /reports, /staff"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="edit-expires-at">
                                        Expiration Date (Optional)
                                    </label>
                                    <input
                                        type="date"
                                        id="edit-expires-at"
                                        name="expires_at"
                                        value={editFormData.expires_at}
                                        onChange={handleEditFormChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeEditModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <div className="spinner-small"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Notification Modal */}
            {showModal && selectedNotification && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <i className={`${getNotificationIcon(selectedNotification.type)} modal-icon ${selectedNotification.type}`}></i>
                                {selectedNotification.title}
                            </h2>
                            <button className="modal-close" onClick={closeModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h3 className="modal-section-title">Notification Details</h3>
                                <div className="modal-info-row">
                                    <span className="modal-label">Type:</span>
                                    <span className={`modal-badge ${selectedNotification.type}`}>
                                        {selectedNotification.type.charAt(0).toUpperCase() + selectedNotification.type.slice(1)}
                                    </span>
                                </div>
                                <div className="modal-info-row">
                                    <span className="modal-label">From:</span>
                                    <span>{selectedNotification.sender_name || 'System'}</span>
                                </div>
                                <div className="modal-info-row">
                                    <span className="modal-label">Sender Type:</span>
                                    <span className="modal-value">{selectedNotification.sender_type?.replace('_', ' ').toUpperCase() || 'N/A'}</span>
                                </div>
                                <div className="modal-info-row">
                                    <span className="modal-label">Sent:</span>
                                    <span>{new Date(selectedNotification.created_at).toLocaleString()}</span>
                                </div>
                                {selectedNotification.expires_at && (
                                    <div className="modal-info-row">
                                        <span className="modal-label">Expires:</span>
                                        <span className="modal-value expires">{new Date(selectedNotification.expires_at).toLocaleString()}</span>
                                    </div>
                                )}
                            </div>

                            <div className="modal-divider"></div>

                            <div className="modal-section">
                                <h3 className="modal-section-title">Target Audience</h3>
                                <div className="modal-info-row">
                                    <span className="modal-label">Target Type:</span>
                                    <span className="modal-badge info">
                                        {selectedNotification.target_type === 'all' ? 'All Users' :
                                         selectedNotification.target_type === 'organization' ? 'Specific Organization' :
                                         selectedNotification.target_type === 'premise' ? 'Specific Premise' :
                                         selectedNotification.target_type}
                                    </span>
                                </div>
                                {selectedNotification.target_org_id && (
                                    <div className="modal-info-row">
                                        <span className="modal-label">Organization:</span>
                                        <span className="modal-value">
                                            {selectedNotification.target_org_name || `ID: ${selectedNotification.target_org_id}`}
                                        </span>
                                    </div>
                                )}
                                {selectedNotification.target_premise_id && (
                                    <div className="modal-info-row">
                                        <span className="modal-label">Premise:</span>
                                        <span className="modal-value">
                                            {selectedNotification.target_premise_name || `ID: ${selectedNotification.target_premise_id}`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="modal-divider"></div>

                            <div className="modal-message">
                                <h3>Message:</h3>
                                <p>{selectedNotification.message}</p>
                            </div>

                            {(selectedNotification.action_url || selectedNotification.action_label) && (
                                <>
                                    <div className="modal-divider"></div>
                                    <div className="modal-section">
                                        <h3 className="modal-section-title">Action</h3>
                                        {selectedNotification.action_label && (
                                            <div className="modal-info-row">
                                                <span className="modal-label">Action Label:</span>
                                                <span className="modal-value">{selectedNotification.action_label}</span>
                                            </div>
                                        )}
                                        {selectedNotification.action_url && (
                                            <div className="modal-info-row">
                                                <span className="modal-label">Action URL:</span>
                                                <span className="modal-value url">{selectedNotification.action_url}</span>
                                            </div>
                                        )}
                                        {selectedNotification.action_url && (
                                            <div className="modal-action">
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => {
                                                        navigate(selectedNotification.action_url);
                                                        closeModal();
                                                    }}
                                                >
                                                    <i className="fas fa-external-link-alt"></i>
                                                    {selectedNotification.action_label || 'Go to Link'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .notification-center {
                    padding: 2rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .header-content h1 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .header-content h1 i {
                    color: #3b82f6;
                }

                .header-content p {
                    margin: 0;
                    color: #6b7280;
                }

                .header-actions {
                    display: flex;
                    gap: 1rem;
                    flex-wrap: wrap;
                }

                .btn {
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    border: none;
                    font-size: 1rem;
                }

                .btn:hover {
                    transform: translateY(-2px);
                }

                .btn-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                }

                .btn-primary:hover {
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
                }

                .btn-success {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                }

                .btn-success:hover {
                    background: linear-gradient(135deg, #059669 0%, #047857 100%);
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
                }

                .message {
                    padding: 1rem 1.25rem;
                    border-radius: 10px;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-weight: 500;
                    animation: slideDown 0.3s ease-out;
                }

                .message.success {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                    border-left: 4px solid #28a745;
                }

                .message.error {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                    border-left: 4px solid #dc3545;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .notifications-container {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    overflow: hidden;
                }

                .notifications-header {
                    background: #f9fafb;
                    border-bottom: 2px solid #e5e7eb;
                    padding: 1rem;
                }

                .filter-tabs {
                    display: flex;
                    gap: 0.5rem;
                }

                .filter-tab {
                    padding: 0.5rem 1rem;
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    color: #6b7280;
                    transition: all 0.2s ease;
                }

                .filter-tab:hover {
                    background: #f3f4f6;
                    border-color: #d1d5db;
                }

                .filter-tab.active {
                    background: #3b82f6;
                    color: white;
                    border-color: #3b82f6;
                }

                .notifications-body {
                    min-height: 400px;
                }

                .loading-state {
                    padding: 4rem;
                    text-align: center;
                    color: #6b7280;
                }

                .spinner-large {
                    width: 50px;
                    height: 50px;
                    border: 4px solid #f3f4f6;
                    border-top: 4px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 1rem;
                }

                @keyframes spin {
                    0% {
                        transform: rotate(0deg);
                    }
                    100% {
                        transform: rotate(360deg);
                    }
                }

                .notifications-list {
                    display: flex;
                    flex-direction: column;
                }

                .notification-card {
                    display: flex;
                    gap: 1rem;
                    padding: 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .notification-card:hover {
                    background: #f9fafb;
                }

                .notification-card.unread {
                    background: rgba(59, 130, 246, 0.05);
                    border-left: 4px solid #3b82f6;
                }

                .notification-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                    flex-shrink: 0;
                }

                .notification-icon.info {
                    background: #dbeafe;
                    color: #3b82f6;
                }

                .notification-icon.success {
                    background: #d1fae5;
                    color: #10b981;
                }

                .notification-icon.warning {
                    background: #fef3c7;
                    color: #f59e0b;
                }

                .notification-icon.error {
                    background: #fee2e2;
                    color: #ef4444;
                }

                .notification-icon.announcement {
                    background: #e9d5ff;
                    color: #a855f7;
                }

                .notification-content {
                    flex: 1;
                    min-width: 0;
                }

                .notification-header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1rem;
                    margin-bottom: 0.5rem;
                }

                .notification-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0;
                }

                .unread-badge {
                    background: #3b82f6;
                    color: white;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .notification-message {
                    color: #4b5563;
                    margin: 0 0 0.75rem 0;
                    line-height: 1.5;
                }

                .notification-meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    font-size: 0.875rem;
                    color: #6b7280;
                }

                .notification-meta span {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                }

                .notification-action {
                    color: #3b82f6;
                    font-weight: 500;
                }

                .empty-state {
                    padding: 4rem;
                    text-align: center;
                    color: #6b7280;
                }

                .empty-state i {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    opacity: 0.3;
                }

                .empty-state h3 {
                    font-size: 1.5rem;
                    margin: 0 0 0.5rem 0;
                    color: #374151;
                }

                .empty-state p {
                    margin: 0;
                }

                .pagination {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 1rem;
                    padding: 1.5rem;
                    border-top: 1px solid #e5e7eb;
                }

                .pagination-btn {
                    padding: 0.5rem 1rem;
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    color: #374151;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .pagination-btn:hover:not(:disabled) {
                    background: #f3f4f6;
                    border-color: #3b82f6;
                    color: #3b82f6;
                }

                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .pagination-info {
                    color: #6b7280;
                    font-weight: 500;
                }

                .notification-actions {
                    display: flex;
                    gap: 0.5rem;
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }

                .notification-card:hover .notification-actions {
                    opacity: 1;
                }

                .action-btn {
                    background: none;
                    border: none;
                    padding: 0.5rem;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 1rem;
                }

                .view-btn {
                    color: #3b82f6;
                }

                .view-btn:hover {
                    background: rgba(59, 130, 246, 0.1);
                    transform: scale(1.1);
                }

                .edit-btn {
                    color: #f59e0b;
                }

                .edit-btn:hover {
                    background: rgba(245, 158, 11, 0.1);
                    transform: scale(1.1);
                }

                .delete-btn {
                    color: #ef4444;
                }

                .delete-btn:hover {
                    background: rgba(239, 68, 68, 0.1);
                    transform: scale(1.1);
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 1rem;
                }

                .modal-content {
                    background: white;
                    border-radius: 12px;
                    max-width: 600px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }

                .modal-header {
                    padding: 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 1rem;
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex: 1;
                }

                .modal-icon {
                    font-size: 1.75rem;
                }

                .modal-icon.success {
                    color: #10b981;
                }

                .modal-icon.warning {
                    color: #f59e0b;
                }

                .modal-icon.error {
                    color: #ef4444;
                }

                .modal-icon.announcement {
                    color: #8b5cf6;
                }

                .modal-icon.info {
                    color: #3b82f6;
                }

                .modal-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #6b7280;
                    padding: 0.25rem;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                }

                .modal-close:hover {
                    background: #f3f4f6;
                    color: #1f2937;
                }

                .modal-body {
                    padding: 1.5rem;
                }

                .modal-section {
                    margin-bottom: 1.5rem;
                }

                .modal-section-title {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 1rem;
                    padding-bottom: 0.5rem;
                    border-bottom: 2px solid #e5e7eb;
                }

                .modal-info-row {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }

                .modal-label {
                    font-weight: 600;
                    color: #4b5563;
                    min-width: 120px;
                }

                .modal-value {
                    color: #1f2937;
                    flex: 1;
                }

                .modal-value.url {
                    color: #3b82f6;
                    word-break: break-all;
                    font-size: 0.875rem;
                }

                .modal-value.expires {
                    color: #f59e0b;
                    font-weight: 500;
                }

                .modal-badge {
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    font-weight: 500;
                }

                .modal-badge.success {
                    background: #d1fae5;
                    color: #065f46;
                }

                .modal-badge.warning {
                    background: #fef3c7;
                    color: #92400e;
                }

                .modal-badge.error {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .modal-badge.announcement {
                    background: #ede9fe;
                    color: #5b21b6;
                }

                .modal-badge.info {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .modal-divider {
                    height: 1px;
                    background: #e5e7eb;
                    margin: 1.5rem 0;
                }

                .modal-message {
                    margin-bottom: 1.5rem;
                }

                .modal-message h3 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #4b5563;
                    margin-bottom: 0.75rem;
                }

                .modal-message p {
                    color: #1f2937;
                    line-height: 1.6;
                    white-space: pre-wrap;
                }

                .modal-action {
                    display: flex;
                    justify-content: flex-end;
                }

                .modal-footer {
                    padding: 1.5rem;
                    border-top: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    background: #f9fafb;
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-group label {
                    display: block;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                }

                .required {
                    color: #ef4444;
                    margin-left: 0.25rem;
                }

                .form-input,
                .form-textarea,
                .form-select {
                    width: 100%;
                    padding: 0.75rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 1rem;
                    color: #1f2937;
                    transition: all 0.2s ease;
                    font-family: inherit;
                }

                .form-input:focus,
                .form-textarea:focus,
                .form-select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-textarea {
                    resize: vertical;
                    min-height: 100px;
                }

                .btn-secondary {
                    background: white;
                    color: #374151;
                    border: 2px solid #e5e7eb;
                }

                .btn-secondary:hover {
                    background: #f3f4f6;
                    border-color: #d1d5db;
                    box-shadow: none;
                }

                .spinner-small {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    display: inline-block;
                }

                @media (max-width: 768px) {
                    .notification-center {
                        padding: 1rem;
                    }

                    .page-header {
                        flex-direction: column;
                    }

                    .header-content h1 {
                        font-size: 1.5rem;
                    }

                    .notification-card {
                        flex-direction: column;
                        gap: 0.75rem;
                    }

                    .notification-meta {
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .notification-actions {
                        opacity: 1;
                        margin-top: 0.5rem;
                    }

                    .modal-content {
                        margin: 1rem;
                        max-height: calc(100vh - 2rem);
                    }

                    .modal-header h2 {
                        font-size: 1.25rem;
                    }

                    .modal-info-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default NotificationCenter;
